/**
 * header.component.ts
 * -------------------
 * Application-wide top header bar.
 * Displays the Chevron logo, application name, and the signed-in user's
 * display name pulled from the active MSAL account.
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule }                  from '@angular/common';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus }             from '@azure/msal-browser';
import { Subject }                       from 'rxjs';
import { filter, takeUntil }             from 'rxjs/operators';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  /** Signed-in user's display name — shown in the top-right corner */
  displayName = '';

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private readonly authService:      MsalService,
    private readonly broadcastService: MsalBroadcastService,
  ) {}

  ngOnInit(): void {
    // Wait until MSAL is no longer in a mid-interaction state before
    // reading the active account, so the name is reliably available
    // after a redirect login completes.
    this.broadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        const account = this.authService.instance.getActiveAccount()
          ?? this.authService.instance.getAllAccounts()[0]
          ?? null;
        this.displayName = account?.name ?? account?.username ?? '';
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}

